import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Card,
  CardContent,
  Stack,
  IconButton,
  Icon,
  Tooltip,
  Button,
  MenuItem,
  Menu
} from 'investira.react.components';
import { dates, arrays, formats, validators } from 'investira.sdk';
import { HighchartsReact } from 'investira.react.charts';
import { CONSOLIDACAO_TIPO } from '@investirapri/financa.js/lib/enums';
import PropTypes from 'prop-types';
import services from '../../../services';
import CardState from './CardState';

/**
 * Renderiza o componente PatrimonioCard.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {string|number} props.pessoaId - Identificador da pessoa para uso standalone.
 * @param {string} props.pessoaNome - Nome usado para compor `aria-label`.
 * @param {string} props.setorId - Setor da empresa para ajustar o titulo.
 * @param {Array} props.historico - Historico de PL usado quando o componente recebe dados por props.
 * @param {boolean} props.isLoading - Estado de carregamento controlado externamente.
 * @param {number} props.patrimonioLiquido - Valor principal exibido no card.
 * @param {number} props.consolidacaoTipo - Consolidacao controlada externamente.
 * @param {Function} props.onChangeConsolidacao - Callback para troca da consolidacao.
 * @param {Object} props.actions - Acoes auxiliares do componente.
 * @param {Function} props.actions.formatNumber - Formatador do valor principal.
 * @param {Function} props.onOpenHistory - Abre o historico do indicador.
 * @param {boolean} props.showChart - Controla exibicao do grafico.
 * @param {Object} props.sx - Estilos adicionais para o wrapper.
 * @param {boolean} props.showHistoryButton - Controla exibicao do atalho de historico.
 *
 * @returns {JSX.Element|null} Elemento renderizado pelo componente.
 */

const PatrimonioCard = memo(props => {
  const params = useParams();
  const {
    pessoaId,
    pessoaNome,
    setorId,
    historico: pHistorico,
    isLoading: pIsLoading,
    patrimonioLiquido,
    consolidacaoTipo: pConsolidacaoTipo,
    onChangeConsolidacao,
    actions: pActions,
    onOpenHistory,
    showChart = true,
    showHistoryButton = true,
    sx,
    ...otherProps
  } = props;

  const xPessoaId = pessoaId || params?.pessoa_id;
  const [historicoLocal, setHistoricoLocal] = useState([]);
  const [isLoadingStandalone, setLoadingStandalone] = useState(false);
  const [consolidacaoTipoLocal, setConsolidacaoTipoLocal] = useState(null);
  const [anchorElConsolidaco, setAnchorElConsolidaco] = useState(null);

  const xHistorico = Array.isArray(pHistorico) ? pHistorico : historicoLocal;
  const openConsolidacao = Boolean(anchorElConsolidaco);

  const xPatrimonio = useMemo(() => {
    if (validators.isEmpty(xHistorico)) {
      return {};
    }

    return arrays.arrayToObject(xHistorico, 'consolidacao_tipo');
  }, [xHistorico]);

  const xOptions = useMemo(() => {
    if (validators.isEmpty(xHistorico)) {
      return [];
    }

    return Array.from(new Set(xHistorico.map(xItem => xItem?.consolidacao_tipo).filter(Boolean)));
  }, [xHistorico]);

  const xConsolidacaoTipo = validators.isNull(pConsolidacaoTipo)
    ? consolidacaoTipoLocal
    : pConsolidacaoTipo;
  const xPatrimonioAtual = xPatrimonio?.[xConsolidacaoTipo];
  const xIsLoading = typeof pIsLoading === 'boolean' ? pIsLoading : isLoadingStandalone;

  const formatData = useCallback((pData = [], pConsolidacaoTipo) => {
    if (validators.isNull(pConsolidacaoTipo) || validators.isEmpty(pData)) {
      return [];
    }

    return pData
      .filter(xItem => xItem?.consolidacao_tipo === pConsolidacaoTipo)
      .map(xPoint => [dates.toDate(xPoint.data).getTime(), xPoint.pl]);
  }, []);

  const xChartOptions = useMemo(
    () => ({
      title: false,
      chart: {
        height: 120,
        margin: [0, 0, 0, 0],
        spacing: [0, 0, 0, 0]
      },
      legend: { enabled: false },
      xAxis: { visible: false },
      yAxis: { visible: false },
      responsive: {
        condition: {
          maxWidth: 500
        }
      },
      series: [
        {
          name: 'PL',
          type: 'line',
          data: formatData(xHistorico, xConsolidacaoTipo),
          marker: {
            enabled: false
          },
          zones: [
            {
              value: 0,
              color: '#ed442c'
            },
            {
              color: '#00dfa8'
            }
          ]
        }
      ],
      tooltip: {
        formatter: function () {
          const xDate = formats.formatDateCustom(this.point.x, 'DD/MMM/YYYY');
          const xValue = formats.friendlyNumber(this.point.y, 2, false);
          const xFullValue = formats.formatNumber(this.point.y, 2, true, false);

          return `<span style="font-size: 1em"><b>${xDate}</b></span><br/><span style="color:${this.color}">●</span>${this.series.name}: <b>${xValue}</b> (${xFullValue})<br/>`;
        }
      }
    }),
    [formatData, xConsolidacaoTipo, xHistorico]
  );

  useEffect(() => {
    if (!validators.isNull(pConsolidacaoTipo)) {
      return;
    }

    if (validators.isEmpty(xOptions)) {
      setConsolidacaoTipoLocal(null);
      return;
    }

    if (validators.isNull(consolidacaoTipoLocal) || !xOptions.includes(consolidacaoTipoLocal)) {
      setConsolidacaoTipoLocal(xOptions[0]);
    }
  }, [consolidacaoTipoLocal, pConsolidacaoTipo, xOptions]);

  useEffect(() => {
    if (Array.isArray(pHistorico)) {
      return;
    }

    if (!xPessoaId) {
      setHistoricoLocal([]);
      setLoadingStandalone(false);
      return;
    }

    const xToday = new Date();

    setLoadingStandalone(true);
    services.pessoas.pl(
      {
        data: null,
        data_inicio: dates.addYears(dates.toSqlDate(xToday), -10),
        data_fim: dates.toSqlDate(xToday),
        pessoa_id: xPessoaId,
        sort: 'data ASC'
      },
      rRes => {
        setHistoricoLocal(rRes?.data || []);
      },
      null,
      () => {
        setLoadingStandalone(false);
      }
    );

    return () => {
      if (services.pessoas?.cancel?.pl?.cancel) {
        services.pessoas.cancel.pl.cancel();
      }
    };
  }, [pHistorico, xPessoaId]);

  const xValue = !validators.isNull(patrimonioLiquido)
    ? patrimonioLiquido
    : xPatrimonioAtual?.pl;
  const xShowCard = !validators.isNull(xValue) || !validators.isEmpty(xPatrimonioAtual);
  const xTitle = setorId === '65.41.3.00' ? 'Patrimônio Social' : 'Patrimônio líquido';

  const formatNumber = pValue => {
    if (validators.isNull(pValue)) {
      return '-';
    }

    if (pActions?.formatNumber) {
      return pActions.formatNumber(pValue);
    }

    return formats.friendlyNumber(pValue, 2, false);
  };

  const handleOpenClick = () => {
    onOpenHistory && onOpenHistory('pl');
  };

  const handleConsolidacaoChange = pValue => {
    if (onChangeConsolidacao) {
      onChangeConsolidacao(pValue);
    }

    if (validators.isNull(pConsolidacaoTipo)) {
      setConsolidacaoTipoLocal(pValue);
    }
  };

  function renderConsolidacaoMenu(pOptions = [], pCurrentConsolidacao) {
    if (validators.isEmpty(pOptions)) {
      return null;
    }

    const xMenu = pOptions
      .map(xId => Object.values(CONSOLIDACAO_TIPO).find(xItem => xItem.id === xId))
      .filter(Boolean)
      .map(xItem => ({
        label: xItem.descricao,
        value: xItem.id
      }));

    const xCurrentLabel = Object.values(CONSOLIDACAO_TIPO).find(
      xItem => xItem.id === pCurrentConsolidacao
    );

    const xProps =
      pOptions.length >= 2
        ? { onClick: pEvent => setAnchorElConsolidaco(pEvent.currentTarget) }
        : { disabled: true };

    return (
      <>
        <Button
          variant="outlined"
          size="small"
          disableElevation
          {...xProps}
          sx={{ fontSize: '0.7rem', padding: '3px 8px' }}>
          {xCurrentLabel?.descricao}
        </Button>
        <Menu
          anchorEl={anchorElConsolidaco}
          open={openConsolidacao}
          onClose={() => setAnchorElConsolidaco(null)}>
          {xMenu.map(xItem => (
            <MenuItem
              key={xItem.value}
              onClick={() => {
                handleConsolidacaoChange(xItem.value);
                setAnchorElConsolidaco(null);
              }}>
              {xItem.label}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  if (!xIsLoading && !xShowCard) {
    return null;
  }

  return (
    <Stack
      sx={{
        flex: 1,
        minWidth: 0,
        ...sx
      }}
      aria-label={pessoaNome ? `${xTitle}: ${pessoaNome}` : xTitle}
      {...otherProps}>
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" spacing={2}>
            <Stack flexGrow={1}>
              <Typography variant="caption" color="textPrimary" component="h4" gutterBottom>
                {xTitle}
              </Typography>
            </Stack>
            {xShowCard && showHistoryButton && (
              <Stack>
                <Tooltip title="Histórico Patrimônio Líquido">
                  <div>
                    <IconButton color="primary" onClick={handleOpenClick}>
                      <Icon iconName="chart_bar" size={16} />
                    </IconButton>
                  </div>
                </Tooltip>
              </Stack>
            )}
          </Stack>

          <CardState
            loading={xIsLoading}
            data={!validators.isNull(xValue) ? { pl: xValue } : xPatrimonioAtual}>
            <Stack spacing={1} justifyContent="space-between">
              <Stack>
                <Typography variant="h4" color="textPrimary" sx={{ fontWeight: 600 }}>
                  {formatNumber(xValue)}
                </Typography>

                {!validators.isNull(xPatrimonioAtual?.data) && (
                  <Typography variant="caption" color="textPrimary" gutterBottom>
                    Data de referência{' '}
                    {formats.formatDateCustom(xPatrimonioAtual?.data, 'DD/MMM/YYYY')}
                  </Typography>
                )}
              </Stack>

              {showChart && (
                <>
                  <Stack flexGrow="1">
                    <HighchartsReact options={xChartOptions} />
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Stack>{renderConsolidacaoMenu(xOptions, xConsolidacaoTipo)}</Stack>
                  </Stack>
                </>
              )}
            </Stack>
          </CardState>
        </CardContent>
      </Card>
    </Stack>
  );
});

PatrimonioCard.displayName = 'PatrimonioCard';

PatrimonioCard.propTypes = {
  pessoaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pessoaNome: PropTypes.string,
  setorId: PropTypes.string,
  historico: PropTypes.array,
  isLoading: PropTypes.bool,
  patrimonioLiquido: PropTypes.number,
  consolidacaoTipo: PropTypes.number,
  onChangeConsolidacao: PropTypes.func,
  actions: PropTypes.shape({
    formatNumber: PropTypes.func
  }),
  onOpenHistory: PropTypes.func,
  showChart: PropTypes.bool,
  sx: PropTypes.object,
  showHistoryButton: PropTypes.bool
};

export default PatrimonioCard;
